import React from 'react';
import styles from './WhyToxbox.module.css';
import FooterComponent from '../Footer/FooterComponent';

const pageData = {
  title: "Why Toxbox AI – The Best Enterprise Conversational AI Platform",
  meta_description: "Toxbox AI is an enterprise conversational AI platform designed to transform customer interactions with AI-powered chatbots, intelligent virtual assistants, and workflow automation.",
  seo_keywords: ["enterprise conversational AI", "AI chatbots", "omnichannel customer engagement", "AI virtual assistants"],
  sections: [
    {
      type: "hero",
      headline: "Why Toxbox AI – The Best Enterprise Conversational AI Platform",
      subheadline: "Toxbox AI is an enterprise conversational AI platform designed to help businesses transform customer interactions with AI-powered chatbots, intelligent virtual assistants, and workflow automation AI. Built for digital transformation, Toxbox delivers omnichannel customer engagement across web, mobile apps, WhatsApp, social media, and voice channels.",
      cta: [
        { text: "Request a Free Demo", link: "#demo" }
      ]
    },
    {
      type: "text",
      headline: "10+ Years of Delivering Enterprise AI Solutions",
      content: "For over a decade, Toxbox AI has empowered enterprises to:",
      items: [
        "Automate customer support with AI chatbots for reducing support costs",
        "Increase sales with AI chatbots for lead generation",
        "Drive workflow automation using machine learning",
        "Improve customer experience with AI-driven virtual assistants for enterprises",
        "Reduce customer response time with AI while ensuring scalability, reliability, and ROI"
      ]
    },
    {
      type: "features",
      headline: "Smarter Conversations. Stronger Results.",
      content: "What makes Toxbox stand out among the best enterprise conversational AI platforms:",
      items: [
        "Natural Language Understanding (NLU) engine – Advanced NLP for business automation that ensures human-like conversations",
        "Seamless integrations – CRM, ERP, HRMS, ticketing systems, and more",
        "Secure conversational AI for regulated industries – GDPR compliant, enterprise-grade data protection",
        "Real-time AI sentiment analysis platform – Know exactly how your customers feel",
        "Enterprise AI platform built for scale – Handles millions of queries with 99.9% uptime"
      ]
    },
    {
      type: "omnichannel",
      headline: "Engage Customers Anywhere, Anytime",
      content: "With Toxbox AI, enterprises achieve true omnichannel customer engagement across:",
      items: [
        "Web & in-app chatbots",
        "WhatsApp Business AI assistants",
        "Voice AI for call centers & IVR",
        "Instagram, Facebook Messenger, Telegram & social channels",
        "SMS & email automation with AI",
        "Deliver consistent, personalized experiences across every touchpoint"
      ]
    },
    {
      type: "insights",
      headline: "Data That Drives Digital Transformation",
      content: "Toxbox AI empowers businesses with AI-powered analytics and optimization:",
      items: [
        "Conversation funnel analysis",
        "Customer sentiment tracking",
        "CSAT & NPS performance dashboards",
        "AI-driven optimization recommendations",
        "Insights that fuel digital transformation with conversational AI",
        "Turn conversations into strategic growth opportunities"
      ]
    },
    {
      type: "enterprise",
      headline: "Built for Scale, Security & Success",
      content: "Toxbox AI is an enterprise AI platform designed for large-scale deployments:",
      items: [
        "Multi-region hosting & global scalability",
        "Enterprise-level compliance & encryption",
        "99.9% uptime SLA for mission-critical systems",
        "APIs for custom workflows and integrations",
        "Trusted as a top AI customer support software solution worldwide"
      ]
    },
    {
      type: "industries",
      headline: "Conversational AI Tailored for Your Industry",
      content: "Toxbox AI specializes in industry-specific conversational AI solutions:",
      items: [
        "AI solutions for fintech automation – Secure, compliant customer interactions",
        "Healthcare virtual assistant AI – Appointment booking, patient engagement",
        "Conversational AI for e-commerce engagement – Personalized shopping experiences, cart recovery",
        "AI-powered chatbots in education – Admissions, enrollment, and tutoring",
        "Travel industry AI chatbot automation – Booking management & multilingual support"
      ]
    },
    {
      type: "results",
      headline: "Proven Impact of Enterprise Conversational AI",
      content: "Our customers achieve measurable outcomes with Toxbox AI:",
      items: [
        "Reduce customer response time with AI by up to 70%",
        "Boost conversion rates with AI chatbots for lead generation",
        "Cut operational costs by automating repetitive queries",
        "Transform customer experience automation across industries",
        "Toxbox is the smarter choice when comparing enterprise AI platform comparison tools"
      ]
    },
    {
      type: "final_cta",
      headline: "Ready to Redefine Customer Engagement with Toxbox AI?",
      content: "Whether you’re exploring Toxbox AI vs traditional chatbots, or comparing top AI customer support software, one thing is clear: Toxbox delivers enterprise-grade conversational AI that drives results.",
      cta: [
        { text: "Request a Personalized Demo", link: "#demo" },
        { text: "Talk to Our Experts Today", link: "#experts" }
      ]
    }
  ]
};

const WhyToxbox = () => {
  return (
    <div className={styles.whyToxbox}>
      {pageData.sections.map((section, index) => {
        switch (section.type) {
          case 'hero':
            return (
              <section key={index} className={styles.hero}>
                <div className={styles.container}>
                  <h1 className={styles.headline}>{section.headline}</h1>
                  <p className={styles.subheadline}>{section.subheadline}</p>
                  <div className={styles.ctaGroup}>
                    {section.cta.map((button, idx) => (
                      <a key={idx} href={button.link} className={styles.cta}>{button.text}</a>
                    ))}
                  </div>
                </div>
              </section>
            );
          case 'text':
            return (
              <section key={index} className={styles.text}>
                <div className={styles.container}>
                  <h2 className={styles.headline}>{section.headline}</h2>
                  <p className={styles.content}>{section.content}</p>
                  <div className={styles.grid}>
                    {section.items.map((item, idx) => (
                      <div key={idx} className={styles.item}>
                        <p className={styles.itemText}>{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            );
          case 'features':
            return (
              <section key={index} className={styles.features}>
                <div className={styles.container}>
                  <h2 className={styles.headline}>{section.headline}</h2>
                  <p className={styles.content}>{section.content}</p>
                  <div className={styles.grid}>
                    {section.items.map((item, idx) => (
                      <div key={idx} className={styles.feature}>
                        <p className={styles.featureText}>{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            );
          case 'omnichannel':
            return (
              <section key={index} className={styles.omnichannel}>
                <div className={styles.container}>
                  <h2 className={styles.headline}>{section.headline}</h2>
                  <p className={styles.content}>{section.content}</p>
                  <div className={styles.grid}>
                    {section.items.map((item, idx) => (
                      <div key={idx} className={styles.omnichannelItem}>
                        <p className={styles.omnichannelText}>{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            );
          case 'insights':
            return (
              <section key={index} className={styles.insights}>
                <div className={styles.container}>
                  <h2 className={styles.headline}>{section.headline}</h2>
                  <p className={styles.content}>{section.content}</p>
                  <div className={styles.grid}>
                    {section.items.map((item, idx) => (
                      <div key={idx} className={styles.insight}>
                        <p className={styles.insightText}>{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            );
          case 'enterprise':
            return (
              <section key={index} className={styles.enterprise}>
                <div className={styles.container}>
                  <h2 className={styles.headline}>{section.headline}</h2>
                  <p className={styles.content}>{section.content}</p>
                  <div className={styles.grid}>
                    {section.items.map((item, idx) => (
                      <div key={idx} className={styles.enterpriseItem}>
                        <p className={styles.enterpriseText}>{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            );
          case 'industries':
            return (
              <section key={index} className={styles.industries}>
                <div className={styles.container}>
                  <h2 className={styles.headline}>{section.headline}</h2>
                  <p className={styles.content}>{section.content}</p>
                  <div className={styles.grid}>
                    {section.items.map((item, idx) => (
                      <div key={idx} className={styles.industry}>
                        <p className={styles.industryText}>{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            );
          case 'results':
            return (
              <section key={index} className={styles.results}>
                <div className={styles.container}>
                  <h2 className={styles.headline}>{section.headline}</h2>
                  <p className={styles.content}>{section.content}</p>
                  <div className={styles.grid}>
                    {section.items.map((item, idx) => (
                      <div key={idx} className={styles.result}>
                        <p className={styles.resultText}>{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            );
          case 'final_cta':
            return (
              <section key={index} className={styles.finalCTA}>
                <div className={styles.container}>
                  <h2 className={styles.headline}>{section.headline}</h2>
                  <p className={styles.content}>{section.content}</p>
                  <div className={styles.ctaGroup}>
                    {section.cta.map((button, idx) => (
                      <a key={idx} href={button.link} className={styles.cta}>{button.text}</a>
                    ))}
                  </div>
                  <img
                    src="https://plus.unsplash.com/premium_photo-1677020308262-4f150d263a08?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0"
                    alt="Start with Toxbox AI"
                    className={styles.image}
                  />
                </div>
              </section>
            );
          default:
            return null;
        }
      })}
    </div>
  );
};

export default WhyToxbox;